
import {
  onBeforeMount, defineComponent, ref, computed, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dropdown from 'primevue/dropdown';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import BaseTextInput from '@/components/fields/BaseTextInput.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { Field } from '@/types/events-types';

export default defineComponent({
  name: 'EventFieldsForm',
  components: {
    DataTable,
    Column,
    Dropdown,
    Checkbox,
    Button,
    BaseTextInput,
  },

  props: {
    fields: {
      type: Array as () => Field[],
      required: true,
    },

    primaryKeys: {
      type: Array as () => string[],
      required: true,
    },

    eventDefinition: {
      type: Object as PropType<any | null>,
      required: true,
    },
  },

  setup(props, { emit }) {
    const { t } = useI18n();

    const fieldsInternal = ref<Field[]>([...props.fields]);
    const primaryKeysInternal = ref([...props.primaryKeys]);

    // Columns

    const columnTableKey = ref(0);

    // Updates primary keys based on the column and its primary key status.
    const updatePrimaryKeys = async (columnName: string, isPrimary: boolean, id: number) => {
      if (columnName) {
        if (isPrimary) {
          if (!primaryKeysInternal.value.includes(columnName)) {
            primaryKeysInternal.value.push(columnName);
          }
        } else {
          primaryKeysInternal.value = primaryKeysInternal.value.filter((key) => key !== columnName);
        }

        emit('update:primaryKeys', primaryKeysInternal.value);
      } else {
        const field = fieldsInternal.value.find((f) => f.id === id);
        if (field) field.primary = false;
        await showToastError(t('events.errors.not_empty_column_name_for_primary_key'));
      }
    };

    const typeAvailable = ref([
      { label: 'Bool', value: 'bool' },
      { label: 'Text', value: 'text' },
      { label: 'LongText', value: 'longtext' },
      { label: 'Number', value: 'number' },
      { label: 'Decimal', value: 'decimal' },
      { label: 'Date', value: 'date' },
      { label: 'JSON', value: 'json' },
    ]);

    // Adds a new column with default values to `fieldsInternal`
    const addColumn = () => {
      fieldsInternal.value.push({
        id: fieldsInternal.value.length + 1,
        name: '',
        type: '',
        options: '',
        description: '',
        primary: false,
        required: true,
      });
    };

    // Removes a column based on its `id` and updates primary keys if necessary
    const removeColumn = (id: number) => {
      const field = fieldsInternal.value.find((field) => field.id === id);

      if (field) {
        if (primaryKeysInternal.value.includes(field.name)) {
          primaryKeysInternal.value = primaryKeysInternal.value.filter((key) => key !== field.name);
          console.log(primaryKeysInternal.value);
          emit('update:primaryKeys', primaryKeysInternal.value);
        }

        fieldsInternal.value = [...fieldsInternal.value];

        field.delete = true;
        emit('update:fields', fieldsInternal.value);
      }

      columnTableKey.value += 1;
    };

    // Emits changes to the list of fields
    const emitFieldChange = () => {
      emit('update:fields', fieldsInternal.value);
    };

    const updateFieldType = (fieldName: string) => {
      emitFieldChange();
    };

    // Filters out deleted fields from `fieldsInternal` and returns a select list if fields have names.
    const fieldOption = computed(() => {
      const filteredFields = fieldsInternal.value.filter((field) => field.delete === false || field.delete === undefined);

      if (!filteredFields.some((field) => field.name)) {
        return [];
      }

      return transformToSelectFieldList(filteredFields);
    });

    // Retrieves the fields of a table based on its name (either from system or custom tables)
    const getReferenceFields = (referenceTable: string) => {
      const table = props.eventDefinition.find((t: any) => t.table_name === referenceTable);

      if (table) {
        return table.fields.map((field: any) => ({
          label: field?.fieldName || field?.name,
          value: field?.fieldName || field?.name,
          disabled: true,
          type: field?.fieldType || field?.type,
        }));
      }

      return [];
    };

    // Transforms an array of fields into a select list format
    const transformToSelectFieldList = (fields: Field[]) => fields.map((field) => ({
      label: field.name,
      value: field.name,
    }));

    return {
      t,
      typeAvailable,
      fieldsInternal,
      updatePrimaryKeys,
      addColumn,
      removeColumn,
      emitFieldChange,
      updateFieldType,
      columnTableKey,
    };
  },
});
